/**
 * RecipesManager — define recipes that link menu products to ingredients.
 *
 * Each recipe line can be either:
 *   • an ingredient  (leaf) — e.g. 18g Ethiopia Yirgacheffe
 *   • a sub-recipe   (node) — e.g. × 1 portion of Espresso
 *
 * Prep recipes are produced via Prep Batches and consumed as linked prep
 * ingredients (not as sub-recipes). Sales deplete prep stock directly; raw
 * ingredients are deducted only when logging a prep batch.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { computePrepStandardCost, type ChoiceSelectionMode } from '@cafe-pos/shared';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { UNITS } from '../constants.js';
import { ArrowLeftRight, BookOpen, Check, ChevronLeft, Layers, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  FormGrid,
  IconButton,
  PageHeader,
  SegmentedControl,
} from '../ui/index.js';

type RecipeRow = Database['public']['Tables']['recipes']['Row'];
type RecipeItemRow = Database['public']['Tables']['recipe_items']['Row'];
type IngredientRow = Database['public']['Tables']['ingredients']['Row'];

// ─── Choice-group types ───────────────────────────────────────────────────────

interface ChoiceOption {
  id: string;
  ingredient_id: string;
  ingredient_name: string;
  ingredient_unit: string;
  quantity: number;
  price_delta: number;
  sort_order: number;
}

interface ChoiceGroupWithOptions {
  id: string;
  recipe_id: string;
  name: string | null;
  sort_order: number;
  selection_mode: ChoiceSelectionMode;
  min_select: number;
  max_select: number | null;
  options: ChoiceOption[];
}

interface ResolvedLeaf {
  name: string;
  qty: number;
  unit: string;
}

interface RecipeItemSubstitute {
  id: string;
  ingredient_id: string;
  ingredient_name: string;
  price_delta: number;
  quantity: number | null;
}

interface RecipeItemDisplay extends RecipeItemRow {
  ingredient_name: string;
  ingredient_unit: string;
  sub_recipe_name: string | null;
  resolved: ResolvedLeaf[];
  /** Line uses a prep ingredient — sales deplete prep stock, not raw. */
  is_prep_stock?: boolean;
  prep_recipe_name?: string | null;
  /** Sub-recipe points at a prep recipe (legacy; prefer prep ingredient). */
  sub_recipe_is_prep_legacy?: boolean;
  /** Swap options the customer can choose at POS time. */
  substitutes: RecipeItemSubstitute[];
}

interface RecipeWithItems extends RecipeRow {
  items: RecipeItemDisplay[];
  is_prep: boolean;
  prep_yield: number | null;
  prep_unit: string | null;
  prep_ingredient_id: string | null;
}

// ─── Unit conversion helpers ──────────────────────────────────────────────────

/** Conversion table: toBase converts to the smallest unit in each group. */
const UNIT_GROUPS: Record<string, { group: string; toBase: number }> = {
  mg:  { group: 'weight', toBase: 0.001 },
  g:   { group: 'weight', toBase: 1 },
  kg:  { group: 'weight', toBase: 1000 },
  ml:  { group: 'volume', toBase: 1 },
  cl:  { group: 'volume', toBase: 10 },
  dl:  { group: 'volume', toBase: 100 },
  L:   { group: 'volume', toBase: 1000 },
};

/** Return all units in the same conversion group as `unit`. */
function getCompatibleUnits(unit: string): string[] {
  const info = UNIT_GROUPS[unit];
  if (!info) return [unit];
  return Object.entries(UNIT_GROUPS)
    .filter(([, v]) => v.group === info.group)
    .map(([u]) => u);
}

/** Convert `value` from `fromUnit` to `toUnit`. Returns `value` unchanged if conversion is impossible. */
function convertQty(value: number, fromUnit: string, toUnit: string): number {
  if (fromUnit === toUnit) return value;
  const from = UNIT_GROUPS[fromUnit];
  const to = UNIT_GROUPS[toUnit];
  if (!from || !to || from.group !== to.group) return value;
  return (value * from.toBase) / to.toBase;
}

// ─────────────────────────────────────────────────────────────────────────────

/** Flatten a recipe tree to leaf ingredients for preview.
 *
 * For prep sub-recipes, the `multiplier` represents the output quantity (e.g. 1 kg of pesto).
 * We divide by `prep_yield` to get the fraction of the batch actually being consumed, then
 * scale each raw ingredient accordingly.
 */
function resolveLeaves(
  recipeId: string,
  multiplier: number,
  recipesMap: Map<string, RecipeWithItems>,
  depth = 0,
): ResolvedLeaf[] {
  if (depth > 10) return [];
  const recipe = recipesMap.get(recipeId);
  if (!recipe) return [];
  // Scale multiplier by 1/prep_yield so that `multiplier` = units of finished output,
  // not a direct coefficient applied to the recipe's ingredient list.
  const effective =
    recipe.is_prep && recipe.prep_yield ? multiplier / recipe.prep_yield : multiplier;
  const out: ResolvedLeaf[] = [];
  for (const it of recipe.items) {
    if (it.sub_recipe_id) {
      out.push(
        ...resolveLeaves(it.sub_recipe_id, effective * Number(it.quantity), recipesMap, depth + 1),
      );
    } else if (it.ingredient_id) {
      const existing = out.find((l) => l.name === it.ingredient_name);
      const qty = effective * Number(it.quantity);
      if (existing) {
        existing.qty += qty;
      } else {
        out.push({ name: it.ingredient_name, qty, unit: it.ingredient_unit });
      }
    }
  }
  return out;
}

function formatResolvedLeaves(leaves: ResolvedLeaf[]): string {
  return leaves
    .map((l) => `${l.qty.toFixed(3).replace(/\.?0+$/, '')} ${l.unit} ${l.name}`)
    .join(', ');
}

interface RecipeLineCost {
  cost: number;
  missing: boolean;
  /** Unit cost used for the line (for tooltip). */
  unitCost?: number;
}

/** Sum leaf ingredient costs for a recipe tree at a given output multiplier. */
function recipeTreeCost(
  recipeId: string,
  multiplier: number,
  recipesMap: Map<string, RecipeWithItems>,
  ingMap: Map<string, IngredientRow>,
  depth = 0,
): RecipeLineCost {
  if (depth > 10) return { cost: 0, missing: true };
  const recipe = recipesMap.get(recipeId);
  if (!recipe) return { cost: 0, missing: true };

  const effective =
    recipe.is_prep && recipe.prep_yield ? multiplier / recipe.prep_yield : multiplier;

  let cost = 0;
  let missing = false;

  for (const it of recipe.items) {
    if (it.sub_recipe_id) {
      const sub = recipeTreeCost(
        it.sub_recipe_id,
        effective * Number(it.quantity),
        recipesMap,
        ingMap,
        depth + 1,
      );
      cost += sub.cost;
      missing = missing || sub.missing;
    } else if (it.ingredient_id) {
      const ing = ingMap.get(it.ingredient_id);
      const unitCost = Number(ing?.cost_price ?? 0);
      const qty = effective * Number(it.quantity);
      if (!ing || unitCost <= 0) missing = true;
      else cost += qty * unitCost;
    }
  }

  return { cost, missing };
}

function recipeLineCost(
  it: RecipeItemDisplay,
  ingredients: IngredientRow[],
  cards: RecipeWithItems[],
): RecipeLineCost {
  const ingMap = new Map(ingredients.map((i) => [i.id, i]));
  const recipesMap = new Map(cards.map((c) => [c.id, c]));
  const qty = Number(it.quantity);

  if (it.sub_recipe_id) {
    const sub = recipesMap.get(it.sub_recipe_id);
    if (!sub) return { cost: 0, missing: true };

    if (sub.is_prep && sub.prep_ingredient_id) {
      const prepIng = ingMap.get(sub.prep_ingredient_id);
      const unitCost = Number(prepIng?.cost_price ?? 0);
      if (prepIng && unitCost > 0) {
        return { cost: qty * unitCost, missing: false, unitCost };
      }
      if (sub.prep_yield) {
        const costMap = new Map(
          ingredients.map((i) => [i.id, { id: i.id, cost_price: Number(i.cost_price), name: i.name }]),
        );
        const result = computePrepStandardCost(sub.prep_yield, sub.items, costMap);
        const bomUnitCost = result.unitCost;
        return {
          cost: qty * bomUnitCost,
          missing: result.missingCosts.length > 0 || result.hasSubRecipes || bomUnitCost <= 0,
          unitCost: bomUnitCost,
        };
      }
    }

    const tree = recipeTreeCost(it.sub_recipe_id, qty, recipesMap, ingMap);
    return { ...tree, unitCost: qty > 0 ? tree.cost / qty : 0 };
  }

  if (it.ingredient_id) {
    const ing = ingMap.get(it.ingredient_id);
    const unitCost = Number(ing?.cost_price ?? 0);
    if (!ing || unitCost <= 0) return { cost: 0, missing: true, unitCost: 0 };
    return { cost: qty * unitCost, missing: false, unitCost };
  }

  return { cost: 0, missing: true };
}

/** Format an ingredient cost as AMD. */
const costAMD = (n: number) => `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} ֏`;

// ─── Ingredient autosuggest input ─────────────────────────────────────────────

function IngredientAutocomplete({
  ingredients,
  value,
  onChange,
  placeholder,
  exclude = [],
  prepIngredientIds,
}: {
  ingredients: IngredientRow[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  exclude?: string[];
  /** Ingredient IDs linked to a prep recipe (produced via prep batches). */
  prepIngredientIds?: Set<string>;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = ingredients.find((i) => i.id === value);
  const prepSuffix = (id: string) =>
    prepIngredientIds?.has(id) ? ' · prep' : '';
  const displayValue = open
    ? query
    : selected
      ? `${selected.name} (${selected.unit})${prepSuffix(selected.id)}`
      : '';
  const filtered = (
    query.trim()
      ? ingredients.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()))
      : ingredients
  ).filter((i) => !exclude.includes(i.id) && (i.active !== false || i.id === value));

  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      setOpenUp(below < 220 && above > below);
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, query, filtered.length]);

  function handleSelect(ing: IngredientRow) {
    onChange(ing.id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div className="ingredient-autocomplete" ref={wrapRef}>
      <input
        ref={inputRef}
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange('');
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder ?? t('recipes.searchIngredient')}
        autoComplete="off"
      />
      {open && (
        <ul
          className={`ingredient-autocomplete-menu${openUp ? ' ingredient-autocomplete-menu--up' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.length === 0 ? (
            <li className="ingredient-autocomplete-empty muted">{t('recipes.ingredientNoMatch')}</li>
          ) : (
            filtered.map((i) => (
              <li
                key={i.id}
                className={value === i.id ? 'selected' : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(i);
                }}
              >
                <span>
                  {i.name} <span className="muted">({i.unit}){prepSuffix(i.id)}</span>
                </span>
                <span className="muted">{costAMD(i.cost_price)}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** Read ?recipe= from the hash (e.g. #/recipes?recipe=uuid). */
function readRecipeIdFromHash(): string | null {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const id = params.get('recipe')?.trim();
  return id || null;
}

function syncRecipeHash(recipeId: string | null) {
  const base = window.location.hash.split('?')[0];
  const next = recipeId ? `${base}?recipe=${encodeURIComponent(recipeId)}` : base;
  window.history.replaceState(null, '', next);
}

function pickRecipeFromHash(cards: RecipeWithItems[]): string | null {
  const id = readRecipeIdFromHash();
  if (!id) return null;
  return cards.some((c) => c.id === id) ? id : null;
}

export function RecipesManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [cards, setCards] = useState<RecipeWithItems[]>([]);
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // New card form
  const [showCreate, setShowCreate] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [newCardIsPrep, setNewCardIsPrep] = useState(false);
  const [newCardPrepYield, setNewCardPrepYield] = useState('');
  const [newCardPrepUnit, setNewCardPrepUnit] = useState('');
  const [cardSaving, setCardSaving] = useState(false);

  // Prep yield inline edit for existing recipes
  const [prepEditId, setPrepEditId] = useState<string | null>(null);
  const [prepEditYield, setPrepEditYield] = useState('');
  const [prepEditUnit, setPrepEditUnit] = useState('');
  const [prepEditSaving, setPrepEditSaving] = useState(false);
  const [prepCostSaving, setPrepCostSaving] = useState(false);

  // Recipe list search
  const [cardSearch, setCardSearch] = useState('');

  // Selected card for editing
  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  // Inline name edit for recipe
  const [nameEditId, setNameEditId] = useState<string | null>(null);
  const [nameEditVal, setNameEditVal] = useState('');

  // Add-line form state
  type LineMode = 'ingredient' | 'sub_recipe';
  const [lineMode, setLineMode] = useState<LineMode>('ingredient');
  const [newItemIngredientId, setNewItemIngredientId] = useState('');
  const [newItemSubRecipeId, setNewItemSubRecipeId] = useState('');
  const [newItemQty, setNewItemQty] = useState('');
  /** Display unit for the quantity input (may differ from the canonical unit; converted on save). */
  const [newItemIngUnit, setNewItemIngUnit] = useState('');
  const [newItemSubUnit, setNewItemSubUnit] = useState('');
  const [itemSaving, setItemSaving] = useState(false);

  // Substitute management (per item, added after item creation)
  const [addingSubFor, setAddingSubFor] = useState<string | null>(null);
  const [addSubIngId, setAddSubIngId] = useState('');
  const [addSubPriceDelta, setAddSubPriceDelta] = useState('');
  const [addSubQty, setAddSubQty] = useState('');
  const [subSaving, setSubSaving] = useState(false);

  // Inline edit state — recipe item
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [editItemIngId, setEditItemIngId] = useState('');
  const [editItemSubRecipeId, setEditItemSubRecipeId] = useState('');
  const [editItemQty, setEditItemQty] = useState('');
  const [editItemSaving, setEditItemSaving] = useState(false);

  // Inline edit state — substitute
  const [editSubId, setEditSubId] = useState<string | null>(null);
  const [editSubIngId, setEditSubIngId] = useState('');
  const [editSubPriceDelta, setEditSubPriceDelta] = useState('');
  const [editSubQty, setEditSubQty] = useState('');
  const [editSubSaving, setEditSubSaving] = useState(false);

  // Choice groups (keyed by recipe_id)
  const [choiceGroups, setChoiceGroups] = useState<Map<string, ChoiceGroupWithOptions[]>>(
    new Map(),
  );
  // Add-group form
  const [addingCG, setAddingCG] = useState(false);
  const [cgSaving, setCGSaving] = useState(false);
  // Add-option form (keyed by group id)
  const [addingOptFor, setAddingOptFor] = useState<string | null>(null);
  const [newOptIngId, setNewOptIngId] = useState('');
  const [newOptQty, setNewOptQty] = useState('');
  const [newOptPriceDelta, setNewOptPriceDelta] = useState('');
  const [optSaving, setOptSaving] = useState(false);
  const [editOptQtyId, setEditOptQtyId] = useState<string | null>(null);
  const [editOptQtyVal, setEditOptQtyVal] = useState('');
  const [editOptQtySaving, setEditOptQtySaving] = useState(false);

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);

    const [cardsRes, itemsRes, ingsRes, subsRes] = await Promise.all([
      supabase.from('recipes').select('*').order('name'),
      supabase.from('recipe_items').select('*'),
      supabase.from('ingredients').select('*').order('name'),
      supabase
        .from('recipe_item_substitutes')
        .select('id, recipe_item_id, ingredient_id, price_delta, quantity'),
    ]);

    if (cardsRes.error) {
      setError(cardsRes.error.message);
      setLoading(false);
      return;
    }
    if (itemsRes.error) {
      setError(itemsRes.error.message);
      setLoading(false);
      return;
    }
    if (ingsRes.error) {
      setError(ingsRes.error.message);
      setLoading(false);
      return;
    }

    const ingMap = new Map(ingsRes.data.map((i) => [i.id, i]));
    const recipeNameMap = new Map(cardsRes.data.map((c) => [c.id, c.name]));

    // Build choice groups map (recipe_id → groups)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cGroupsRes = await (supabase as any)
      .from('recipe_choice_groups')
      .select('id, recipe_id, name, sort_order, selection_mode, min_select, max_select')
      .order('sort_order');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cGroupsRows: any[] = cGroupsRes.error
      ? ((
          await (supabase as any)
            .from('recipe_choice_groups')
            .select('id, recipe_id, name, sort_order')
            .order('sort_order')
        ).data ?? [])
      : (cGroupsRes.data ?? []);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groupIds: string[] = cGroupsRows.map((g) => g.id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let cOptionsData: any[] = [];
    if (groupIds.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cOptsRes = await (supabase as any)
        .from('recipe_choice_options')
        .select('id, group_id, ingredient_id, quantity, price_delta, sort_order')
        .in('group_id', groupIds)
        .order('sort_order');
      cOptionsData = cOptsRes.data ?? [];
    }
    const optsByGroup = new Map<string, ChoiceOption[]>();
    for (const o of cOptionsData) {
      if (!optsByGroup.has(o.group_id)) optsByGroup.set(o.group_id, []);
      const ing = ingMap.get(o.ingredient_id);
      optsByGroup.get(o.group_id)!.push({
        id: o.id,
        ingredient_id: o.ingredient_id,
        ingredient_name: ing?.name ?? '—',
        ingredient_unit: ing?.unit ?? '',
        quantity: Number(o.quantity),
        price_delta: o.price_delta ?? 0,
        sort_order: o.sort_order,
      });
    }
    const cgByRecipe = new Map<string, ChoiceGroupWithOptions[]>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const g of cGroupsRows) {
      if (!cgByRecipe.has(g.recipe_id)) cgByRecipe.set(g.recipe_id, []);
      cgByRecipe.get(g.recipe_id)!.push({
        id: g.id,
        recipe_id: g.recipe_id,
        name: g.name,
        sort_order: g.sort_order,
        selection_mode: g.selection_mode === 'combine' ? 'combine' : 'separate',
        min_select: Number(g.min_select) || 1,
        max_select: g.max_select == null ? null : Number(g.max_select),
        options: optsByGroup.get(g.id) ?? [],
      });
    }
    setChoiceGroups(cgByRecipe);

    // Build recipe_item_id → RecipeItemSubstitute[]
    const subsMap = new Map<string, RecipeItemSubstitute[]>();
    for (const s of subsRes.data ?? []) {
      if (!subsMap.has(s.recipe_item_id)) subsMap.set(s.recipe_item_id, []);
      subsMap.get(s.recipe_item_id)!.push({
        id: s.id,
        ingredient_id: s.ingredient_id,
        ingredient_name: ingMap.get(s.ingredient_id)?.name ?? '—',
        price_delta: s.price_delta ?? 0,
        quantity: s.quantity,
      });
    }

    // First pass: build cards
    const built: RecipeWithItems[] = cardsRes.data.map((c) => ({
      ...c,
      is_prep: (c as any).is_prep ?? false,
      prep_yield: (c as any).prep_yield ?? null,
      prep_unit: (c as any).prep_unit ?? null,
      prep_ingredient_id: (c as any).prep_ingredient_id ?? null,
      items: itemsRes.data
        .filter((it) => it.recipe_id === c.id)
        .map((it) => ({
          ...it,
          ingredient_name: it.ingredient_id ? (ingMap.get(it.ingredient_id)?.name ?? '—') : '',
          ingredient_unit: it.ingredient_id ? (ingMap.get(it.ingredient_id)?.unit ?? '') : '',
          sub_recipe_name: it.sub_recipe_id ? (recipeNameMap.get(it.sub_recipe_id) ?? null) : null,
          resolved: [],
          substitutes: subsMap.get(it.id) ?? [],
        })),
    }));

    // Second pass: resolve preview amounts (sub-recipes + prep ingredients)
    const recipesMap = new Map(built.map((c) => [c.id, c]));
    for (const c of built) {
      for (const it of c.items) {
        if (it.sub_recipe_id) {
          it.resolved = resolveLeaves(it.sub_recipe_id, Number(it.quantity), recipesMap);
          const sub = recipesMap.get(it.sub_recipe_id);
          if (sub?.is_prep) it.sub_recipe_is_prep_legacy = true;
        } else if (it.ingredient_id) {
          const prepRecipe = built.find(
            (r) => r.is_prep && r.prep_ingredient_id === it.ingredient_id,
          );
          if (prepRecipe) {
            it.is_prep_stock = true;
            it.prep_recipe_name = prepRecipe.name;
            // Informational: raw per batch recipe at this output qty (sales deplete prep stock).
            it.resolved = resolveLeaves(prepRecipe.id, Number(it.quantity), recipesMap);
          }
        }
      }
    }

    setCards(built);
    setIngredients(ingsRes.data);
    const picked = pickRecipeFromHash(built);
    if (picked) {
      setSelectedCard(picked);
      setCardSearch('');
    }
    setLoading(false);
  }

  function noteSaved() {
    setSavedFlash(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedFlash(false), 2200);
  }

  function reloadSaved() {
    noteSaved();
    void load();
  }

  useEffect(() => {
    void load();
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  useEffect(() => {
    function syncFromHash() {
      const picked = pickRecipeFromHash(cards);
      if (picked) {
        setSelectedCard(picked);
        setCardSearch('');
      }
    }
    window.addEventListener('popstate', syncFromHash);
    window.addEventListener('hashchange', syncFromHash);
    return () => {
      window.removeEventListener('popstate', syncFromHash);
      window.removeEventListener('hashchange', syncFromHash);
    };
  }, [cards]);

  function selectRecipe(id: string | null) {
    setSelectedCard(id);
    syncRecipeHash(id);
  }

  async function recalculatePrepStandardCost(recipe: RecipeWithItems) {
    if (!supabase || !recipe.prep_ingredient_id || !recipe.prep_yield) return;
    setPrepCostSaving(true);
    setError(null);
    const costMap = new Map(
      ingredients.map((i) => [i.id, { id: i.id, cost_price: Number(i.cost_price), name: i.name }]),
    );
    const result = computePrepStandardCost(recipe.prep_yield, recipe.items, costMap);
    if (result.missingCosts.length > 0) {
      setError(
        `${t('recipes.prepCostMissing')}: ${result.missingCosts.map((m) => m.name).join(', ')}`,
      );
      setPrepCostSaving(false);
      return;
    }
    const unitCost = Math.round(result.unitCost * 100) / 100;
    const { error: err } = await supabase
      .from('ingredients')
      .update({ cost_price: unitCost })
      .eq('id', recipe.prep_ingredient_id);
    setPrepCostSaving(false);
    if (err) setError(err.message);
    else reloadSaved();
  }

  async function createCard() {
    if (!supabase || !clientId) return;
    if (!newCardName.trim()) {
      setError('Card name is required.');
      return;
    }
    if (newCardIsPrep && (!newCardPrepYield || !newCardPrepUnit.trim())) {
      setError('Prep recipes need a yield quantity and unit.');
      return;
    }
    setCardSaving(true);
    setError(null);

    // If marked as prep, auto-create the linked ingredient first.
    let prepIngredientId: string | null = null;
    if (newCardIsPrep) {
      const { data: ing, error: ingErr } = await supabase
        .from('ingredients')
        .insert({
          client_id: clientId,
          name: newCardName.trim(),
          unit: newCardPrepUnit.trim(),
          stock: 0,
          low_stock_threshold: undefined,
          cost_price: 0,
        })
        .select('id')
        .single();
      if (ingErr || !ing) {
        setError(ingErr?.message ?? 'Failed to create prep ingredient');
        setCardSaving(false);
        return;
      }
      prepIngredientId = ing.id;
    }

    const { data, error: err } = await supabase
      .from('recipes')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .insert({
        client_id: clientId,
        name: newCardName.trim(),
        is_prep: newCardIsPrep,
        prep_yield: newCardIsPrep ? Number(newCardPrepYield) : undefined,
        prep_unit: newCardIsPrep ? newCardPrepUnit.trim() : undefined,
        prep_ingredient_id: prepIngredientId ?? undefined,
      } as any)
      .select()
      .single();
    setCardSaving(false);
    if (err || !data) {
      // Roll back orphaned ingredient if recipe insert failed.
      if (prepIngredientId) {
        await supabase.from('ingredients').delete().eq('id', prepIngredientId);
      }
      setError(err?.message ?? 'Failed');
      return;
    }
    setNewCardName('');
    setNewCardIsPrep(false);
    setNewCardPrepYield('');
    setNewCardPrepUnit('');
    setShowCreate(false);
    setSelectedCard(data.id);
    syncRecipeHash(data.id);
    reloadSaved();
  }

  async function deleteCard(id: string) {
    if (!supabase) return;
    const card = cards.find((c) => c.id === id);
    if (!confirm(`Delete recipe "${card?.name ?? ''}"? This cannot be undone.`)) return;
    const { error: err } = await supabase.from('recipes').delete().eq('id', id);
    if (err) setError(err.message);
    else {
      if (selectedCard === id) selectRecipe(null);
      void load();
    }
  }

  async function saveRecipeName(id: string) {
    if (!supabase) return;
    const trimmed = nameEditVal.trim();
    if (!trimmed) return;
    const { error: err } = await supabase.from('recipes').update({ name: trimmed }).eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setNameEditId(null);
    reloadSaved();
  }

  async function savePrepEdit(id: string) {
    if (!supabase || !clientId) return;
    const qty = Number(prepEditYield);
    if (!qty || qty <= 0 || !prepEditUnit.trim()) {
      setError('Prep yield and unit are required.');
      return;
    }
    setPrepEditSaving(true);
    setError(null);
    const recipe = cards.find((c) => c.id === id);
    // First time enabling prep: create linked ingredient
    if (!recipe?.prep_ingredient_id) {
      const { data: ing, error: ingErr } = await supabase
        .from('ingredients')
        .insert({
          client_id: clientId,
          name: recipe?.name ?? '',
          unit: prepEditUnit.trim(),
          stock: 0,
          low_stock_threshold: undefined,
          cost_price: 0,
        })
        .select('id')
        .single();
      if (ingErr || !ing) {
        setError(ingErr?.message ?? 'Failed to create prep ingredient');
        setPrepEditSaving(false);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from('recipes')
        .update({
          is_prep: true,
          prep_yield: qty,
          prep_unit: prepEditUnit.trim(),
          prep_ingredient_id: ing.id,
        })
        .eq('id', id);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from('recipes')
        .update({ prep_yield: qty, prep_unit: prepEditUnit.trim() })
        .eq('id', id);
      // Keep ingredient unit in sync
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from('ingredients')
        .update({ unit: prepEditUnit.trim() })
        .eq('id', recipe.prep_ingredient_id);
    }
    setPrepEditSaving(false);
    setPrepEditId(null);
    reloadSaved();
  }

  async function addItem() {
    if (!supabase || !clientId || !selectedCard) return;
    const rawQty = parseFloat(newItemQty);
    if (!newItemQty || isNaN(rawQty) || rawQty <= 0) {
      setError('Enter a positive quantity.');
      return;
    }

    if (lineMode === 'sub_recipe') {
      if (!newItemSubRecipeId) {
        setError('Select a sub-recipe.');
        return;
      }
      const subRecipe = cards.find((c) => c.id === newItemSubRecipeId);
      if (subRecipe?.is_prep) {
        setError(t('recipes.prepSubRecipeBlocked'));
        return;
      }
      // Cycle check
      const recipesMap = new Map(cards.map((c) => [c.id, c]));
      if (
        newItemSubRecipeId === selectedCard ||
        recipe_would_create_cycle(selectedCard, newItemSubRecipeId, recipesMap)
      ) {
        setError('Adding this sub-recipe would create a cycle.');
        return;
      }
      // Convert displayed unit → sub-recipe's canonical prep_unit before saving
      const sr = availableSubRecipes.find((c) => c.id === newItemSubRecipeId);
      const qty =
        sr?.prep_unit && newItemSubUnit ? convertQty(rawQty, newItemSubUnit, sr.prep_unit) : rawQty;
      setItemSaving(true);
      setError(null);
      const { error: err } = await supabase.from('recipe_items').insert({
        client_id: clientId,
        recipe_id: selectedCard,
        sub_recipe_id: newItemSubRecipeId,
        ingredient_id: null,
        quantity: qty,
      });
      setItemSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    } else {
      if (!newItemIngredientId) {
        setError('Select an ingredient.');
        return;
      }
      // Convert displayed unit → ingredient's canonical unit before saving
      const ing = ingredients.find((i) => i.id === newItemIngredientId);
      if (ing && ing.active === false) {
        setError('That ingredient is inactive.');
        return;
      }
      const qty =
        ing && newItemIngUnit ? convertQty(rawQty, newItemIngUnit, ing.unit) : rawQty;
      setItemSaving(true);
      setError(null);
      const { error: err } = await supabase.from('recipe_items').insert({
        client_id: clientId,
        recipe_id: selectedCard,
        ingredient_id: newItemIngredientId,
        sub_recipe_id: null,
        quantity: qty,
      });
      setItemSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    }
    setNewItemIngredientId('');
    setNewItemSubRecipeId('');
    setNewItemQty('');
    setNewItemIngUnit('');
    setNewItemSubUnit('');
    reloadSaved();
  }

  async function deleteItem(id: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('recipe_items').delete().eq('id', id);
    if (err) setError(err.message);
    else reloadSaved();
  }

  async function addSubstitute(itemId: string, baseIngId: string) {
    if (!supabase || !clientId || !addSubIngId) {
      setError('Select a substitute ingredient.');
      return;
    }
    if (addSubIngId === baseIngId) {
      setError('Substitute must differ from the base ingredient.');
      return;
    }
    setSubSaving(true);
    setError(null);
    const { error: err } = await supabase.from('recipe_item_substitutes').insert({
      client_id: clientId,
      recipe_item_id: itemId,
      ingredient_id: addSubIngId,
      price_delta: Number(addSubPriceDelta) || 0,
      quantity: addSubQty ? Number(addSubQty) : null,
    });
    setSubSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAddingSubFor(null);
    setAddSubIngId('');
    setAddSubPriceDelta('');
    setAddSubQty('');
    reloadSaved();
  }

  async function deleteSubstitute(subId: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('recipe_item_substitutes').delete().eq('id', subId);
    if (err) setError(err.message);
    else reloadSaved();
  }

  function startEditItem(it: RecipeItemDisplay) {
    setEditItemId(it.id);
    setEditItemIngId(it.ingredient_id ?? '');
    setEditItemSubRecipeId(it.sub_recipe_id ?? '');
    setEditItemQty(String(it.quantity));
    // Close any open substitute or add-sub panel
    setAddingSubFor(null);
    setEditSubId(null);
  }

  function cancelEditItem() {
    setEditItemId(null);
    setEditItemIngId('');
    setEditItemSubRecipeId('');
    setEditItemQty('');
  }

  async function saveEditItem(it: RecipeItemDisplay) {
    if (!supabase) return;
    const qty = parseFloat(editItemQty);
    if (isNaN(qty) || qty <= 0) {
      setError('Enter a positive quantity.');
      return;
    }

    if (it.sub_recipe_id) {
      // Sub-recipe line: only quantity changes (ingredient field is n/a)
      setEditItemSaving(true);
      setError(null);
      const { error: err } = await supabase
        .from('recipe_items')
        .update({ quantity: qty })
        .eq('id', it.id);
      setEditItemSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    } else {
      if (!editItemIngId) {
        setError('Select an ingredient.');
        return;
      }
      setEditItemSaving(true);
      setError(null);
      const { error: err } = await supabase
        .from('recipe_items')
        .update({ ingredient_id: editItemIngId, quantity: qty })
        .eq('id', it.id);
      setEditItemSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    }

    cancelEditItem();
    reloadSaved();
  }

  function startEditSub(s: RecipeItemSubstitute) {
    setEditSubId(s.id);
    setEditSubIngId(s.ingredient_id);
    setEditSubPriceDelta(s.price_delta > 0 ? String(s.price_delta) : '');
    setEditSubQty(s.quantity != null ? String(s.quantity) : '');
    setAddingSubFor(null);
  }

  function cancelEditSub() {
    setEditSubId(null);
    setEditSubIngId('');
    setEditSubPriceDelta('');
    setEditSubQty('');
  }

  async function saveEditSub(baseIngId: string) {
    if (!supabase || !editSubId) return;
    if (!editSubIngId) {
      setError('Select a substitute ingredient.');
      return;
    }
    if (editSubIngId === baseIngId) {
      setError('Substitute must differ from the base ingredient.');
      return;
    }
    setEditSubSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('recipe_item_substitutes')
      .update({
        ingredient_id: editSubIngId,
        price_delta: Number(editSubPriceDelta) || 0,
        quantity: editSubQty ? Number(editSubQty) : null,
      })
      .eq('id', editSubId);
    setEditSubSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    cancelEditSub();
    reloadSaved();
  }

  /** Client-side cycle guard (mirrors the DB function). */
  function recipe_would_create_cycle(
    parent: string,
    child: string,
    recipesMap: Map<string, RecipeWithItems>,
  ): boolean {
    const visited = new Set<string>();
    const queue = [child];
    while (queue.length) {
      const cur = queue.shift()!;
      if (cur === parent) return true;
      if (visited.has(cur)) continue;
      visited.add(cur);
      const r = recipesMap.get(cur);
      if (r) {
        for (const it of r.items) {
          if (it.sub_recipe_id) queue.push(it.sub_recipe_id);
        }
      }
    }
    return false;
  }

  async function addChoiceGroup() {
    if (!supabase || !clientId || !selectedCard) return;
    setCGSaving(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any).from('recipe_choice_groups').insert({
      client_id: clientId,
      recipe_id: selectedCard,
    });
    setCGSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAddingCG(false);
    reloadSaved();
  }

  async function deleteChoiceGroup(groupId: string) {
    if (!supabase) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any)
      .from('recipe_choice_groups')
      .delete()
      .eq('id', groupId);
    if (err) setError(err.message);
    else reloadSaved();
  }

  async function saveChoiceGroupSettings(
    groupId: string,
    patch: {
      selection_mode?: ChoiceSelectionMode;
      min_select?: number;
      max_select?: number | null;
    },
  ) {
    if (!supabase) return;
    setChoiceGroups((prev) => {
      const next = new Map(prev);
      for (const [recipeId, groups] of next) {
        next.set(
          recipeId,
          groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
        );
      }
      return next;
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any)
      .from('recipe_choice_groups')
      .update(patch)
      .eq('id', groupId);
    if (err) {
      setError(err.message);
      void load();
    } else {
      noteSaved();
    }
  }

  async function addChoiceOption(groupId: string) {
    if (!supabase || !clientId || !newOptIngId) {
      setError('Select an ingredient.');
      return;
    }
    const qty = parseFloat(newOptQty);
    if (!newOptQty || isNaN(qty) || qty <= 0) {
      setError('Enter a positive quantity.');
      return;
    }
    setOptSaving(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any).from('recipe_choice_options').insert({
      client_id: clientId,
      group_id: groupId,
      ingredient_id: newOptIngId,
      quantity: qty,
      price_delta: Number(newOptPriceDelta) || 0,
    });
    setOptSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAddingOptFor(null);
    setNewOptIngId('');
    setNewOptQty('');
    setNewOptPriceDelta('');
    reloadSaved();
  }

  async function saveChoiceOptionQty(optId: string) {
    if (!supabase) return;
    const qty = parseFloat(editOptQtyVal);
    if (!editOptQtyVal || isNaN(qty) || qty <= 0) {
      setError('Enter a positive quantity.');
      return;
    }
    setEditOptQtySaving(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any)
      .from('recipe_choice_options')
      .update({ quantity: qty })
      .eq('id', optId);
    setEditOptQtySaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setEditOptQtyId(null);
    setEditOptQtyVal('');
    reloadSaved();
  }

  async function deleteChoiceOption(optId: string) {
    if (!supabase) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = await (supabase as any)
      .from('recipe_choice_options')
      .delete()
      .eq('id', optId);
    if (err) setError(err.message);
    else reloadSaved();
  }

  const selected = cards.find((c) => c.id === selectedCard) ?? null;
  const selectedPrepCost = useMemo(() => {
    if (!selected?.is_prep || !selected.prep_yield) return null;
    const costMap = new Map(
      ingredients.map((i) => [i.id, { id: i.id, cost_price: Number(i.cost_price), name: i.name }]),
    );
    return computePrepStandardCost(selected.prep_yield, selected.items, costMap);
  }, [selected, ingredients]);
  const selectedRecipeCost = useMemo(() => {
    if (!selected) return { cost: 0, missing: false };
    let cost = 0;
    let missing = false;
    for (const it of selected.items) {
      const line = recipeLineCost(it, ingredients, cards);
      cost += line.cost;
      missing = missing || line.missing;
    }
    return { cost, missing };
  }, [selected, ingredients, cards]);
  const linkedPrepIngredient = selected?.prep_ingredient_id
    ? ingredients.find((i) => i.id === selected.prep_ingredient_id)
    : null;
  // Recipes available as sub-recipes (exclude the selected one to prevent trivial cycles)
  const prepIngredientIds = new Set(
    cards
      .filter((c) => c.is_prep && c.prep_ingredient_id)
      .map((c) => c.prep_ingredient_id as string),
  );
  const prepRecipeByIngredientId = new Map(
    cards
      .filter((c) => c.is_prep && c.prep_ingredient_id)
      .map((c) => [c.prep_ingredient_id as string, c]),
  );

  const availableSubRecipes = cards.filter((c) => {
    if (c.id === selectedCard) return false;
    if (c.is_prep) return false;
    const recipesMap = new Map(cards.map((r) => [r.id, r]));
    return !recipe_would_create_cycle(selectedCard ?? '', c.id, recipesMap);
  });

  const visibleCards = cards.filter((c) =>
    c.name.toLowerCase().includes(cardSearch.trim().toLowerCase()),
  );
  const recipeLineCount = (recipeId: string, itemsLength: number) => {
    const choiceOpts = (choiceGroups.get(recipeId) ?? []).reduce((s, g) => s + g.options.length, 0);
    return itemsLength + choiceOpts;
  };
  const countLabel = (n: number) => `${n} ${n === 1 ? t('recipes.item') : t('recipes.items')}`;
  const unitOptions = UNITS.map((u) => (
    <option key={u} value={u}>
      {u}
    </option>
  ));

  const renderNameEditor = (id: string) => (
    <span className="recipe-name-edit">
      <input
        type="text"
        value={nameEditVal}
        autoFocus
        onChange={(e) => setNameEditVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void saveRecipeName(id);
          if (e.key === 'Escape') setNameEditId(null);
        }}
      />
      <IconButton label={t('recipes.saveName')} size="sm" onClick={() => void saveRecipeName(id)}>
        <Check />
      </IconButton>
      <IconButton label={t('common.cancel')} size="sm" onClick={() => setNameEditId(null)}>
        <X />
      </IconButton>
    </span>
  );

  const renderPrepYieldEditor = (id: string, submitLabel: ReactNode) => (
    <span className="recipe-inline-form">
      <input
        type="number"
        min="0.001"
        step="0.001"
        placeholder={t('recipes.yield')}
        value={prepEditYield}
        onChange={(e) => setPrepEditYield(e.target.value)}
        style={{ width: 90 }}
        autoFocus
      />
      <select value={prepEditUnit} onChange={(e) => setPrepEditUnit(e.target.value)} style={{ width: 90 }}>
        <option value="">Unit…</option>
        {unitOptions}
      </select>
      <Button variant="primary" size="sm" onClick={() => void savePrepEdit(id)} disabled={prepEditSaving}>
        {prepEditSaving ? '…' : submitLabel}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setPrepEditId(null)}>
        Cancel
      </Button>
    </span>
  );

  const renderLineLabel = (it: RecipeItemDisplay) => {
    if (it.sub_recipe_id) {
      return (
        <>
          <div className="recipe-line-name">
            <Layers className="ui-i" /> {it.sub_recipe_name ?? '—'}
          </div>
          <div className="recipe-line-sub">
            {it.sub_recipe_is_prep_legacy && (
              <span className="text-warning">{t('recipes.legacyPrepSubRecipe')} · </span>
            )}
            {it.resolved.length > 0 ? formatResolvedLeaves(it.resolved) : '—'}
          </div>
        </>
      );
    }
    return (
      <>
        <div className="recipe-line-name">
          {it.ingredient_name}
          {it.is_prep_stock && (
            <span className="badge badge-success" title={it.prep_recipe_name ?? undefined}>
              Prep
            </span>
          )}
        </div>
        {it.is_prep_stock && it.resolved.length > 0 && (
          <div className="recipe-line-sub">
            {t('recipes.batchEquivalent')}: {formatResolvedLeaves(it.resolved)}
          </div>
        )}
      </>
    );
  };

  const renderLineQty = (it: RecipeItemDisplay) => {
    if (it.sub_recipe_id) {
      const unit = cards.find((c) => c.id === it.sub_recipe_id)?.prep_unit ?? 'portion';
      return `× ${it.quantity} ${unit}`;
    }
    return `${it.quantity} ${it.ingredient_unit}`;
  };

  const renderLineCost = (it: RecipeItemDisplay) => {
    const { cost, missing, unitCost } = recipeLineCost(it, ingredients, cards);
    if (missing || cost <= 0) {
      return (
        <span className="muted" title={t('recipes.costMissing')}>
          —
        </span>
      );
    }
    const qty = Number(it.quantity);
    const title =
      unitCost != null && unitCost > 0
        ? `${unitCost.toLocaleString('en-US', { maximumFractionDigits: 4 })} × ${qty}`
        : undefined;
    return (
      <span className="recipe-line-cost" title={title}>
        {costAMD(cost)}
      </span>
    );
  };

  const renderEditLineCost = (it: RecipeItemDisplay) => {
    if (it.sub_recipe_id) return renderLineCost(it);
    const ing = ingredients.find((i) => i.id === editItemIngId);
    const qty = Number(editItemQty);
    const unitCost = Number(ing?.cost_price ?? 0);
    if (!ing || !editItemQty || unitCost <= 0) {
      return (
        <span className="muted" title={t('recipes.costMissing')}>
          —
        </span>
      );
    }
    return (
      <span
        className="recipe-line-cost"
        title={`${unitCost.toLocaleString('en-US', { maximumFractionDigits: 4 })} × ${qty}`}
      >
        {costAMD(qty * unitCost)}
      </span>
    );
  };

  const renderSubForm = (
    it: RecipeItemDisplay,
    mode: 'add' | 'edit',
  ) => {
    const isAdd = mode === 'add';
    const ingId = isAdd ? addSubIngId : editSubIngId;
    const setIngId = isAdd ? setAddSubIngId : setEditSubIngId;
    const price = isAdd ? addSubPriceDelta : editSubPriceDelta;
    const setPrice = isAdd ? setAddSubPriceDelta : setEditSubPriceDelta;
    const qty = isAdd ? addSubQty : editSubQty;
    const setQty = isAdd ? setAddSubQty : setEditSubQty;
    const saving = isAdd ? subSaving : editSubSaving;
    const exclude = [
      it.ingredient_id!,
      ...it.substitutes.filter((s) => isAdd || s.id !== editSubId).map((s) => s.ingredient_id),
    ];
    const cancel = isAdd
      ? () => {
          setAddingSubFor(null);
          setAddSubIngId('');
          setAddSubPriceDelta('');
          setAddSubQty('');
        }
      : cancelEditSub;
    const submit = isAdd
      ? () => void addSubstitute(it.id, it.ingredient_id!)
      : () => void saveEditSub(it.ingredient_id!);
    return (
      <tr className="recipe-row-form">
        <td colSpan={5}>
          <div className="recipe-inline-form">
            <span className="recipe-inline-label">
              {isAdd ? 'Substitute for' : 'Edit substitute for'} <strong>{it.ingredient_name}</strong>
            </span>
            <IngredientAutocomplete
              ingredients={ingredients}
              value={ingId}
              onChange={setIngId}
              prepIngredientIds={prepIngredientIds}
              placeholder={t('recipes.substitutePlaceholder')}
              exclude={exclude}
            />
            <input
              type="number"
              min="0"
              step="any"
              placeholder={`Qty (${it.ingredient_unit})`}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              style={{ width: 110 }}
            />
            <input
              type="number"
              min="0"
              step="any"
              placeholder={t('recipes.pricePlus')}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              style={{ width: 100 }}
            />
            <Button variant="primary" size="sm" onClick={submit} disabled={saving || !ingId}>
              {saving ? '…' : isAdd ? 'Add' : 'Save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <section className="ui-stack recipes-page">
      <PageHeader
        title={t('recipes.title')}
        subtitle={t('recipes.description')}
        actions={
          <Button
            variant="primary"
            icon={<Plus />}
            onClick={() => setShowCreate((v) => !v)}
            aria-expanded={showCreate}
          >
            {t('recipes.createCard')}
          </Button>
        }
      />
      {savedFlash && (
        <div className="save-toast" role="status">
          <Check className="save-toast-icon" />
          {t('recipes.autosaved')}
        </div>
      )}

      {showCreate && (
        <Card>
          <CardHeader
            title={t('recipes.newTitle')}
            caption={t('recipes.newCaption')}
            right={
              <IconButton label={t('common.close')} size="sm" onClick={() => setShowCreate(false)}>
                <X />
              </IconButton>
            }
          />
          <CardBody>
            <div className="recipe-create">
              <FormGrid>
                <Field label={t('recipes.name')} htmlFor="recipe-new-name">
                  <input
                    id="recipe-new-name"
                    placeholder={t('recipes.newCardName')}
                    value={newCardName}
                    autoFocus
                    onChange={(e) => setNewCardName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !newCardIsPrep) void createCard();
                    }}
                  />
                </Field>
                {newCardIsPrep && (
                  <>
                    <Field label={t('recipes.batchYield')} htmlFor="recipe-new-yield">
                      <input
                        id="recipe-new-yield"
                        type="number"
                        min="0.001"
                        step="0.001"
                        placeholder={t('recipes.yieldPlaceholder')}
                        value={newCardPrepYield}
                        onChange={(e) => setNewCardPrepYield(e.target.value)}
                      />
                    </Field>
                    <Field label={t('recipes.unit')} htmlFor="recipe-new-unit">
                      <select
                        id="recipe-new-unit"
                        value={newCardPrepUnit}
                        onChange={(e) => setNewCardPrepUnit(e.target.value)}
                      >
                        <option value="">{t('recipes.unitPlaceholder')}</option>
                        {unitOptions}
                      </select>
                    </Field>
                  </>
                )}
              </FormGrid>
              <label className="recipe-check">
                <input
                  type="checkbox"
                  checked={newCardIsPrep}
                  onChange={(e) => setNewCardIsPrep(e.target.checked)}
                />
                <span>
                  {t('recipes.prepRecipe')}
                  <span className="muted"> — {t('recipes.prepHint')}</span>
                </span>
              </label>
              <div className="recipe-create-actions">
                <Button
                  variant="primary"
                  onClick={createCard}
                  disabled={cardSaving || !clientId || !newCardName.trim()}
                >
                  {cardSaving ? '…' : t('recipes.createCard')}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreate(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="tech-cards-layout">
        {/* Left: recipe list */}
        <aside className="tech-card-list">
          <div className="recipe-list-search">
            <Search />
            <input
              type="search"
              placeholder={t('common.search')}
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <p className="muted card-list-empty">{t('common.loading')}</p>
          ) : cards.length === 0 ? (
            <EmptyState
              compact
              icon={<BookOpen />}
              title={t('recipes.noMatchTitle')}
              text={t('recipes.noMatchText')}
            />
          ) : visibleCards.length === 0 ? (
            <p className="muted card-list-empty">No recipes match “{cardSearch}”.</p>
          ) : (
            <ul className="card-list">
              {visibleCards.map((c) => (
                <li
                  key={c.id}
                  className={`card-list-item${selectedCard === c.id ? ' active' : ''}`}
                  onClick={() => {
                    if (nameEditId !== c.id) selectRecipe(c.id);
                  }}
                >
                  {nameEditId === c.id ? (
                    <span onClick={(e) => e.stopPropagation()} style={{ flex: 1 }}>
                      {renderNameEditor(c.id)}
                    </span>
                  ) : (
                    <>
                      <span className="card-list-name">
                        {c.name}
                        {c.is_prep && <span className="badge badge-success">Prep</span>}
                      </span>
                      <span className="card-list-count">{countLabel(recipeLineCount(c.id, c.items.length))}</span>
                      <span className="card-list-actions">
                        <button
                          type="button"
                          title={t('recipes.rename')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setNameEditId(c.id);
                            setNameEditVal(c.name);
                          }}
                        >
                          <Pencil className="ui-i" />
                        </button>
                        <button
                          type="button"
                          className="danger"
                          title={t('recipes.delete')}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCard(c.id);
                          }}
                        >
                          <Trash2 className="ui-i" />
                        </button>
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Right: editor */}
        <div className={`tech-card-detail recipe-detail${selected ? ' is-open' : ''}`}>
          {selected && (
            <button type="button" className="recipe-back" onClick={() => selectRecipe(null)}>
              <ChevronLeft />
              Recipes
            </button>
          )}
          {!selected ? (
            <Card>
              <EmptyState icon={<BookOpen />} title={t('recipes.selectTitle')} text={t('recipes.selectHint')} />
            </Card>
          ) : (
            <>
              {/* ── Header ── */}
              <header className="recipe-head">
                <div className="recipe-head-text">
                  {nameEditId === selected.id ? (
                    renderNameEditor(selected.id)
                  ) : (
                    <h2 className="recipe-head-title">
                      {selected.name}
                      {selected.is_prep && <span className="badge badge-success">Prep</span>}
                    </h2>
                  )}
                  <p className="recipe-head-meta">
                    {countLabel(recipeLineCount(selected.id, selected.items.length))}
                    {selected.is_prep
                      ? ` · yields ${selected.prep_yield ?? '?'} ${selected.prep_unit ?? ''}`
                      : ' · sold directly'}
                  </p>
                </div>
                <div className="recipe-head-actions">
                  <IconButton
                    label={t('recipes.rename')}
                    onClick={() => {
                      setNameEditId(selected.id);
                      setNameEditVal(selected.name);
                    }}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton label={t('recipes.deleteRecipe')} danger onClick={() => deleteCard(selected.id)}>
                    <Trash2 />
                  </IconButton>
                </div>
              </header>

              {/* ── Prep ── */}
              {selected.is_prep ? (
                <Callout
                  tone="info"
                  title={t('recipes.prepRecipe')}
                  actions={
                    selectedPrepCost ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={
                          prepCostSaving ||
                          selectedPrepCost.missingCosts.length > 0 ||
                          selectedPrepCost.unitCost <= 0
                        }
                        onClick={() => void recalculatePrepStandardCost(selected)}
                      >
                        {prepCostSaving ? '…' : t('recipes.recalcPrepCost')}
                      </Button>
                    ) : undefined
                  }
                >
                  <div className="recipe-prep-row">
                    <span>{t('recipes.yields')}</span>
                    {prepEditId === selected.id ? (
                      renderPrepYieldEditor(selected.id, <Check />)
                    ) : (
                      <>
                        <strong>
                          {selected.prep_yield ?? '?'} {selected.prep_unit ?? '?'}
                        </strong>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => {
                            setPrepEditId(selected.id);
                            setPrepEditYield(String(selected.prep_yield ?? ''));
                            setPrepEditUnit(selected.prep_unit ?? '');
                          }}
                        >
                          Change
                        </Button>
                      </>
                    )}
                    {selected.prep_ingredient_id && (
                      <span className="muted">· {t('recipes.linkedAuto')}</span>
                    )}
                  </div>
                  {selectedPrepCost && (
                    <div className="recipe-prep-cost">
                      <span>
                        {t('recipes.prepStandardBatch')}:{' '}
                        <strong>
                          {selectedPrepCost.batchCost.toLocaleString('en-US', { maximumFractionDigits: 2 })} ֏
                        </strong>{' '}
                        / {selected.prep_yield} {selected.prep_unit}
                      </span>
                      <span>
                        {t('recipes.prepStandardUnit')}:{' '}
                        <strong>
                          {selectedPrepCost.unitCost.toLocaleString('en-US', { maximumFractionDigits: 2 })} ֏
                        </strong>
                        /{selected.prep_unit}
                      </span>
                      {linkedPrepIngredient && (
                        <span>
                          {t('recipes.prepStoredCost')}:{' '}
                          <strong>
                            {Number(linkedPrepIngredient.cost_price).toLocaleString('en-US', {
                              maximumFractionDigits: 2,
                            })}{' '}
                            ֏
                          </strong>
                          /{linkedPrepIngredient.unit}
                        </span>
                      )}
                      {selectedPrepCost.missingCosts.length > 0 && (
                        <span className="text-warning">
                          {t('recipes.prepCostMissing')}:{' '}
                          {selectedPrepCost.missingCosts.map((m) => m.name).join(', ')}
                        </span>
                      )}
                      {selectedPrepCost.hasSubRecipes && (
                        <span className="text-warning">{t('recipes.prepCostSubRecipes')}</span>
                      )}
                    </div>
                  )}
                </Callout>
              ) : prepEditId === selected.id ? (
                <div className="recipe-prep-enable">
                  <span className="recipe-inline-label">{t('recipes.batchYield')}</span>
                  {renderPrepYieldEditor(selected.id, 'Enable prep')}
                </div>
              ) : (
                <p className="recipe-prep-hint">
                  Sold directly — stock is deducted per sale.
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => {
                      setPrepEditId(selected.id);
                      setPrepEditYield('');
                      setPrepEditUnit('');
                    }}
                  >
                    {t('recipes.makePrep')}
                  </Button>
                </p>
              )}

              {/* ── Ingredients ── */}
              <Card layout="flush" className="recipe-ingredients-card">
                <CardHeader
                  title={t('recipes.ingredientsTitle')}
                  caption={
                    selected.is_prep
                      ? `Quantities are per batch of ${selected.prep_yield ?? '?'} ${selected.prep_unit ?? 'unit'}`
                      : 'Quantities are per portion sold'
                  }
                />
                <div className="recipe-ingredients-body">
                {selected.items.length === 0 ? (
                  <EmptyState
                    compact
                    title={t('recipes.emptyTitle')}
                    text={t('recipes.emptyText')}
                  />
                ) : (
                  <table className="table recipe-table">
                    <thead>
                      <tr>
                        <th>{t('recipes.colIngredient')}</th>
                        <th className="num">{selected.is_prep ? t('recipes.qtyPerBatch') : t('recipes.colQty')}</th>
                        <th className="num">{t('recipes.colCost')}</th>
                        <th>Substitutes</th>
                        <th aria-label={t('common.actions')} />
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items.map((it) => {
                        const isEditing = editItemId === it.id;
                        const editingSub = it.substitutes.find((s) => s.id === editSubId) ?? null;
                        const addingSub = addingSubFor === it.id;

                        if (isEditing) {
                          return (
                            <tr key={it.id} className="recipe-row-editing">
                              <td>
                                {it.sub_recipe_id ? (
                                  <div className="recipe-line-name">
                                    <Layers className="ui-i" /> {it.sub_recipe_name ?? '—'}
                                  </div>
                                ) : (
                                  <IngredientAutocomplete
                                    ingredients={ingredients}
                                    value={editItemIngId}
                                    onChange={setEditItemIngId}
                                    prepIngredientIds={prepIngredientIds}
                                  />
                                )}
                              </td>
                              <td className="num">
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={editItemQty}
                                  onChange={(e) => setEditItemQty(e.target.value)}
                                  autoFocus
                                  style={{ width: 90 }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void saveEditItem(it);
                                    if (e.key === 'Escape') cancelEditItem();
                                  }}
                                />
                              </td>
                              <td className="num">{renderEditLineCost(it)}</td>
                              <td className="muted">{t('recipes.saveForSubstitutes')}</td>
                              <td>
                                <div className="ui-row-actions">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => void saveEditItem(it)}
                                    disabled={editItemSaving}
                                  >
                                    {editItemSaving ? '…' : 'Save'}
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={cancelEditItem}>
                                    Cancel
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <Fragment key={it.id}>
                            <tr>
                              <td>{renderLineLabel(it)}</td>
                              <td className="num">{renderLineQty(it)}</td>
                              <td className="num">{renderLineCost(it)}</td>
                              <td>
                                {it.ingredient_id ? (
                                  <div className="recipe-subs">
                                    {it.substitutes.map((s) => (
                                      <span key={s.id} className="recipe-sub-chip">
                                        <ArrowLeftRight className="ui-i" />
                                        <button
                                          type="button"
                                          className="recipe-sub-chip-label"
                                          title={t('recipes.editSubstitute')}
                                          onClick={() => startEditSub(s)}
                                        >
                                          {s.ingredient_name}
                                          {s.quantity != null && (
                                            <span className="muted">
                                              {' '}
                                              {s.quantity} {it.ingredient_unit}
                                            </span>
                                          )}
                                          {s.price_delta > 0 && (
                                            <span className="price-delta"> +{s.price_delta} ֏</span>
                                          )}
                                        </button>
                                        <button
                                          type="button"
                                          className="recipe-sub-chip-remove"
                                          title={t('recipes.removeSubstitute')}
                                          onClick={() => deleteSubstitute(s.id)}
                                        >
                                          <X />
                                        </button>
                                      </span>
                                    ))}
                                    {!addingSub && !editingSub && (
                                      <button
                                        type="button"
                                        className="recipe-sub-add"
                                        onClick={() => setAddingSubFor(it.id)}
                                      >
                                        <Plus className="ui-i" /> Substitute
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="muted">—</span>
                                )}
                              </td>
                              <td>
                                <div className="ui-row-actions">
                                  <IconButton label={t('recipes.editLine')} size="sm" onClick={() => startEditItem(it)}>
                                    <Pencil />
                                  </IconButton>
                                  <IconButton
                                    label={t('common.delete')}
                                    size="sm"
                                    danger
                                    onClick={() => deleteItem(it.id)}
                                  >
                                    <Trash2 />
                                  </IconButton>
                                </div>
                              </td>
                            </tr>
                            {addingSub && renderSubForm(it, 'add')}
                            {editingSub && renderSubForm(it, 'edit')}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    {selected.items.length > 0 && (
                      <tfoot>
                        <tr className="recipe-cost-total">
                          <td>
                            <strong>{t('recipes.totalCost')}</strong>
                          </td>
                          <td />
                          <td className="num">
                            {selectedRecipeCost.cost > 0 ? (
                              <>
                                <strong>{costAMD(selectedRecipeCost.cost)}</strong>
                                {selectedRecipeCost.missing && (
                                  <span
                                    className="text-warning recipe-cost-partial"
                                    title={t('recipes.costMissing')}
                                  >
                                    {' '}
                                    *
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
                </div>

                {/* Add line */}
                <div className="recipe-add-line">
                  <SegmentedControl
                    size="sm"
                    ariaLabel="Line type"
                    value={lineMode}
                    onChange={setLineMode}
                    options={[
                      { value: 'ingredient', label: t('recipes.colIngredient') },
                      { value: 'sub_recipe', label: 'Sub-recipe' },
                    ]}
                  />
                  {lineMode === 'ingredient' ? (
                    <>
                      <IngredientAutocomplete
                        ingredients={ingredients}
                        value={newItemIngredientId}
                        prepIngredientIds={prepIngredientIds}
                        onChange={(id) => {
                          setNewItemIngredientId(id);
                          const ing = ingredients.find((i) => i.id === id);
                          setNewItemIngUnit(ing?.unit ?? '');
                        }}
                      />
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder={t('recipes.qty')}
                        value={newItemQty}
                        onChange={(e) => setNewItemQty(e.target.value)}
                        style={{ width: 90 }}
                      />
                      {(() => {
                        const selIng = ingredients.find((i) => i.id === newItemIngredientId);
                        if (!selIng) return null;
                        const compatible = getCompatibleUnits(selIng.unit);
                        return compatible.length > 1 ? (
                          <select
                            value={newItemIngUnit || selIng.unit}
                            onChange={(e) => setNewItemIngUnit(e.target.value)}
                            style={{ width: 72 }}
                          >
                            {compatible.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="muted recipe-unit">{selIng.unit}</span>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      <select
                        value={newItemSubRecipeId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setNewItemSubRecipeId(id);
                          const sr = availableSubRecipes.find((c) => c.id === id);
                          setNewItemSubUnit(sr?.prep_unit ?? '');
                        }}
                        style={{ minWidth: 200 }}
                      >
                        <option value="">Select recipe…</option>
                        {availableSubRecipes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0.0001"
                        step="0.1"
                        placeholder={t('recipes.qty')}
                        value={newItemQty}
                        onChange={(e) => setNewItemQty(e.target.value)}
                        style={{ width: 90 }}
                      />
                      {(() => {
                        const sr = availableSubRecipes.find((c) => c.id === newItemSubRecipeId);
                        const compatible = sr?.prep_unit ? getCompatibleUnits(sr.prep_unit) : [];
                        return compatible.length > 1 ? (
                          <select
                            value={newItemSubUnit || (sr?.prep_unit ?? '')}
                            onChange={(e) => setNewItemSubUnit(e.target.value)}
                            style={{ width: 72 }}
                          >
                            {compatible.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="muted recipe-unit">{sr?.prep_unit ?? 'portions'}</span>
                        );
                      })()}
                    </>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Plus />}
                    onClick={addItem}
                    disabled={
                      itemSaving ||
                      (lineMode === 'ingredient'
                        ? !newItemIngredientId || !newItemQty
                        : !newItemSubRecipeId || !newItemQty)
                    }
                  >
                    {itemSaving ? '…' : 'Add'}
                  </Button>
                </div>
                {(() => {
                  if (lineMode === 'ingredient') {
                    if (!newItemIngredientId || !prepRecipeByIngredientId.has(newItemIngredientId)) return null;
                    const prep = prepRecipeByIngredientId.get(newItemIngredientId)!;
                    const selIng = ingredients.find((i) => i.id === newItemIngredientId);
                    const rawQty = parseFloat(newItemQty);
                    if (!selIng || isNaN(rawQty) || rawQty <= 0) return null;
                    const canonicalQty =
                      newItemIngUnit && selIng.unit ? convertQty(rawQty, newItemIngUnit, selIng.unit) : rawQty;
                    const recipesMap = new Map(cards.map((c) => [c.id, c]));
                    const leaves = resolveLeaves(prep.id, canonicalQty, recipesMap);
                    return (
                      <p className="recipe-add-hint">
                        {t('recipes.prepStockOnSale')}: {canonicalQty} {selIng.unit} {selIng.name}
                        {leaves.length > 0 && ` · ${t('recipes.batchEquivalent')}: ${formatResolvedLeaves(leaves)}`}
                      </p>
                    );
                  }
                  if (!newItemSubRecipeId) return null;
                  const sr = availableSubRecipes.find((c) => c.id === newItemSubRecipeId);
                  const recipesMap = new Map(cards.map((c) => [c.id, c]));
                  const rawQty = parseFloat(newItemQty) || 1;
                  const canonicalQty =
                    sr?.prep_unit && newItemSubUnit ? convertQty(rawQty, newItemSubUnit, sr.prep_unit) : rawQty;
                  const leaves = resolveLeaves(newItemSubRecipeId, canonicalQty, recipesMap);
                  return leaves.length > 0 ? (
                    <p className="recipe-add-hint">= {formatResolvedLeaves(leaves)}</p>
                  ) : null;
                })()}
              </Card>

              {/* ── Choice group ── */}
              {(() => {
                const groups = choiceGroups.get(selected.id) ?? [];
                return (
                  <Card layout="flush">
                    <CardHeader
                      title={t('recipes.choiceTitle')}
                      caption={t('recipes.choiceHint')}
                      right={
                        groups.length === 0 && !addingCG ? (
                          <Button variant="secondary" size="sm" icon={<Plus />} onClick={() => setAddingCG(true)}>
                            {t('recipes.addChoice')}
                          </Button>
                        ) : undefined
                      }
                    />
                    <CardBody>
                      {addingCG && (
                        <div className="recipe-inline-form recipe-cg-create">
                          <span className="muted">
                            Each option has its own deduction quantity in that ingredient&apos;s unit.
                          </span>
                          <Button variant="primary" size="sm" onClick={() => void addChoiceGroup()} disabled={cgSaving}>
                            {cgSaving ? '…' : 'Create group'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setAddingCG(false)}>
                            Cancel
                          </Button>
                        </div>
                      )}

                      {groups.length === 0 && !addingCG && (
                        <p className="choice-empty">No choice group. Most recipes don&apos;t need one.</p>
                      )}

                      {groups.map((group) => (
                        <div key={group.id} className="choice-group">
                          <div className="choice-group-head">
                            <span className="choice-group-title">
                              {group.selection_mode === 'combine'
                                ? t('recipes.choiceModeCombine')
                                : t('recipes.choiceModeSeparate')}
                              <span className="choice-group-meta">
                                {group.options.length} {group.options.length === 1 ? 'option' : 'options'}
                              </span>
                            </span>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                confirm('Delete this choice group and all its options?') &&
                                void deleteChoiceGroup(group.id)
                              }
                            >
                              {t('recipes.deleteGroup')}
                            </Button>
                          </div>

                          <div className="choice-group-body">
                            <div className="choice-group-settings">
                              <SegmentedControl
                                size="sm"
                                ariaLabel={t('recipes.choiceMode')}
                                value={group.selection_mode}
                                onChange={(mode) =>
                                  void saveChoiceGroupSettings(group.id, { selection_mode: mode })
                                }
                                options={[
                                  { value: 'separate', label: t('recipes.choiceModeSeparate') },
                                  { value: 'combine', label: t('recipes.choiceModeCombine') },
                                ]}
                              />
                              <label className="choice-minmax">
                                {t('recipes.choiceMin')}
                                <input
                                  type="number"
                                  min={1}
                                  value={group.min_select}
                                  onChange={(e) => {
                                    const min_select = Math.max(1, Number(e.target.value) || 1);
                                    const max_select =
                                      group.max_select != null && group.max_select < min_select
                                        ? min_select
                                        : group.max_select;
                                    void saveChoiceGroupSettings(group.id, { min_select, max_select });
                                  }}
                                />
                              </label>
                              <label className="choice-minmax">
                                {t('recipes.choiceMax')}
                                <input
                                  type="number"
                                  min={group.min_select}
                                  placeholder={t('recipes.choiceMaxNone')}
                                  value={group.max_select ?? ''}
                                  onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    const max_select =
                                      raw === '' ? null : Math.max(group.min_select, Number(raw) || group.min_select);
                                    void saveChoiceGroupSettings(group.id, { max_select });
                                  }}
                                />
                              </label>
                            </div>
                            <p className="choice-mode-hint">
                              {group.selection_mode === 'combine'
                                ? t('recipes.choiceModeCombineHint')
                                : t('recipes.choiceModeSeparateHint')}
                            </p>
                            <div className="choice-options">
                              {group.options.length === 0 && (
                                <span className="choice-options-empty">{t('recipes.noOptions')}</span>
                              )}
                              {group.options.map((opt) => {
                                const unitCost =
                                  ingredients.find((i) => i.id === opt.ingredient_id)?.cost_price ?? 0;
                                const lineCost = unitCost * opt.quantity;
                                const editingQty = editOptQtyId === opt.id;
                                return (
                                  <span key={opt.id} className="choice-chip">
                                    {opt.ingredient_name}
                                    {editingQty ? (
                                      <>
                                        <input
                                          type="number"
                                          min="0.0001"
                                          step="any"
                                          value={editOptQtyVal}
                                          onChange={(e) => setEditOptQtyVal(e.target.value)}
                                          style={{ width: 72, margin: '0 4px', padding: '2px 6px' }}
                                          disabled={editOptQtySaving}
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') void saveChoiceOptionQty(opt.id);
                                            if (e.key === 'Escape') {
                                              setEditOptQtyId(null);
                                              setEditOptQtyVal('');
                                            }
                                          }}
                                        />
                                        <span className="choice-chip-unit">{opt.ingredient_unit}</span>
                                        <Button
                                          variant="text"
                                          size="sm"
                                          disabled={editOptQtySaving}
                                          onClick={() => void saveChoiceOptionQty(opt.id)}
                                        >
                                          {editOptQtySaving ? '…' : 'Save'}
                                        </Button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        className="choice-chip-unit choice-chip-qty"
                                        title={t('recipes.editDeduction')}
                                        onClick={() => {
                                          setEditOptQtyId(opt.id);
                                          setEditOptQtyVal(String(opt.quantity));
                                        }}
                                      >
                                        {opt.quantity} {opt.ingredient_unit}
                                      </button>
                                    )}
                                    <span className="choice-chip-cost" title={`${unitCost} × ${opt.quantity}`}>
                                      {costAMD(lineCost)}
                                    </span>
                                    {opt.price_delta > 0 && (
                                      <span className="choice-chip-delta">+{opt.price_delta} ֏</span>
                                    )}
                                    <button
                                      type="button"
                                      className="choice-chip-remove"
                                      onClick={() => void deleteChoiceOption(opt.id)}
                                      title={t('recipes.removeOption')}
                                    >
                                      <X />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>

                            {addingOptFor === group.id ? (
                              <div className="choice-add-option">
                                <IngredientAutocomplete
                                  ingredients={ingredients}
                                  value={newOptIngId}
                                  onChange={(id) => {
                                    setNewOptIngId(id);
                                    const ing = ingredients.find((i) => i.id === id);
                                    if (ing && !newOptQty) setNewOptQty('1');
                                  }}
                                  prepIngredientIds={prepIngredientIds}
                                  placeholder={t('recipes.ingredientPlaceholder')}
                                  exclude={group.options.map((o) => o.ingredient_id)}
                                />
                                <input
                                  type="number"
                                  min="0.0001"
                                  step="any"
                                  placeholder={t('recipes.qty')}
                                  value={newOptQty}
                                  onChange={(e) => setNewOptQty(e.target.value)}
                                  style={{ width: 80 }}
                                />
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  placeholder={t('recipes.pricePlus')}
                                  value={newOptPriceDelta}
                                  onChange={(e) => setNewOptPriceDelta(e.target.value)}
                                  style={{ width: 100 }}
                                />
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => void addChoiceOption(group.id)}
                                  disabled={optSaving || !newOptIngId || !newOptQty}
                                >
                                  {optSaving ? '…' : 'Add'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setAddingOptFor(null);
                                    setNewOptIngId('');
                                    setNewOptQty('');
                                    setNewOptPriceDelta('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="choice-add-option">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  icon={<Plus />}
                                  onClick={() => {
                                    setAddingOptFor(group.id);
                                    setNewOptIngId('');
                                    setNewOptQty('');
                                    setNewOptPriceDelta('');
                                  }}
                                >
                                  {t('recipes.addOption')}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardBody>
                  </Card>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
